import {
  Cartesian2,
  Cartesian3,
  Cartographic,
  EllipsoidGeodesic,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Color,
  CallbackProperty,
  LabelStyle,
  VerticalOrigin,
  HeightReference,
  Matrix4,
  Transforms,
  Math as CesiumMath,
  PolylineDashMaterialProperty,
  CustomDataSource,
} from "cesium";

function pickCartesian(viewer, windowPosition) {
  const scene = viewer.scene;
  let p = null;

  if (scene.pickPositionSupported) {
    p = scene.pickPosition(windowPosition);
    if (p) return p;
  }

  const ray = viewer.camera.getPickRay(windowPosition);
  if (!ray) return null;

  return scene.globe.pick(ray, scene);
}

function metersBetween(a, b) {
  try {
    const ca = Cartographic.fromCartesian(a);
    const cb = Cartographic.fromCartesian(b);
    const g = new EllipsoidGeodesic(ca, cb);
    return g.surfaceDistance || 0;
  } catch {
    return Cartesian3.distance(a, b) || 0;
  }
}

function toLonLatHeight(cart) {
  const c = Cartographic.fromCartesian(cart);
  return [CesiumMath.toDegrees(c.longitude), CesiumMath.toDegrees(c.latitude), c.height || 0];
}

export function drawBoxCenterLineOnce({ viewer, dataSource, onLength }) {
  let handler = null;
  let previewLine = null;
  let previewLabel = null;
  let previewDs = null;

  let start = null;
  let end = null;
  let cancelled = false;

  const cleanup = () => {
    try {
      if (previewDs) viewer.dataSources.remove(previewDs, true);
    } catch {}
    previewDs = null;

    previewLine = null;
    previewLabel = null;

    try {
      handler?.destroy?.();
    } catch {}
    handler = null;
  };

  const promise = new Promise((resolve, reject) => {
    if (!viewer || !dataSource?.entities) {
      reject("invalid");
      return;
    }

    handler = new ScreenSpaceEventHandler(viewer.canvas);
    previewDs = new CustomDataSource("box-preview");
    viewer.dataSources.add(previewDs);

    const startRef = { current: null };
    const endRef = { current: null };
    const lenRef = { current: 0 };

    const positionsCb = new CallbackProperty(() => {
      if (!startRef.current || !endRef.current) return [];
      return [startRef.current, endRef.current];
    }, false);

    previewLine = previewDs.entities.add({
      polyline: {
        positions: positionsCb,
        width: 3,
        material: new PolylineDashMaterialProperty({
          color: Color.YELLOW.withAlpha(0.9),
          gapColor: Color.TRANSPARENT,
          dashLength: 16,
        }),
        clampToGround: true,
      },
    });

    previewLabel = previewDs.entities.add({
      position: new CallbackProperty(() => {
        if (!startRef.current || !endRef.current) return startRef.current || endRef.current || Cartesian3.ZERO;
        return Cartesian3.midpoint(startRef.current, endRef.current, new Cartesian3());
      }, false),
      label: {
        text: new CallbackProperty(() => `${(lenRef.current || 0).toFixed(2)} m`, false),
        font: "16px sans-serif",
        style: LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 3,
        fillColor: Color.WHITE,
        outlineColor: Color.BLACK,
        verticalOrigin: VerticalOrigin.BOTTOM,
        pixelOffset: new Cartesian2(0, -10),
        disableDepthTestDistance: 1e9,
        showBackground: true,
        backgroundColor: Color.BLACK.withAlpha(0.6),
        backgroundPadding: new Cartesian2(6, 4),
        show: new CallbackProperty(() => !!startRef.current, false),
      },
    });

    const updatePreview = (pos) => {
      if (!startRef.current) return;
      endRef.current = pos;

      const len = metersBetween(startRef.current, endRef.current);
      lenRef.current = len;
      if (typeof onLength === "function") onLength(len);
    };

    handler.setInputAction((click) => {
      if (cancelled) return;

      const p = pickCartesian(viewer, click.position);
      if (!p) return;

      if (!start) {
        start = p;
        startRef.current = p;
        endRef.current = p;
        lenRef.current = 0;
        return;
      }

      end = p;
      endRef.current = p;

      const lengthMeters = metersBetween(start, end);

      cleanup();
      resolve({ start, end, lengthMeters });
    }, ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction((movement) => {
      if (cancelled) return;
      if (!startRef.current) return;

      const p = pickCartesian(viewer, movement.endPosition);
      if (!p) return;

      updatePreview(p);
    }, ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      cancelled = true;
      cleanup();
      reject("cancelled");
    }, ScreenSpaceEventType.RIGHT_CLICK);
  });

  promise.cancel = () => {
    cancelled = true;
    cleanup();
  };

  return promise;
}

export function buildBoxPolygonFromCenterLine({ start, end, widthMeters }) {
  const half = (Number(widthMeters) || 0) / 2;
  if (!start || !end || !isFinite(half) || half <= 0) return null;

  const mid = Cartesian3.midpoint(start, end, new Cartesian3());
  const enu = Transforms.eastNorthUpToFixedFrame(mid);
  const inv = Matrix4.inverse(enu, new Matrix4());

  const sL = Matrix4.multiplyByPoint(inv, start, new Cartesian3());
  const eL = Matrix4.multiplyByPoint(inv, end, new Cartesian3());

  const dir = Cartesian3.subtract(eL, sL, new Cartesian3());
  dir.z = 0;
  const mag = Cartesian3.magnitude(dir);
  if (!isFinite(mag) || mag === 0) return null;
  Cartesian3.divideByScalar(dir, mag, dir);

  const perp = new Cartesian3(-dir.y, dir.x, 0);
  Cartesian3.multiplyByScalar(perp, half, perp);

  const aL = Cartesian3.add(sL, perp, new Cartesian3());
  const bL = Cartesian3.subtract(sL, perp, new Cartesian3());
  const cL = Cartesian3.subtract(eL, perp, new Cartesian3());
  const dL = Cartesian3.add(eL, perp, new Cartesian3());

  const A = Matrix4.multiplyByPoint(enu, aL, new Cartesian3());
  const B = Matrix4.multiplyByPoint(enu, bL, new Cartesian3());
  const C = Matrix4.multiplyByPoint(enu, cL, new Cartesian3());
  const D = Matrix4.multiplyByPoint(enu, dL, new Cartesian3());

  const ring = [toLonLatHeight(A), toLonLatHeight(B), toLonLatHeight(C), toLonLatHeight(D), toLonLatHeight(A)];

  const geojson = {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [ring] },
  };

  return { geojson, ring, corners: { A, B, C, D } };
}
